import { useVal } from '../../val/val.client';
import darksideVal from '../../settings/darkside.val';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { generateImageFeed } from './utils';
import { getRandomIntInclusive } from '../../utils/utils';
import { FadeInImage } from './FadeInImage';
import Masonry from 'react-masonry-css';
import { ImageModal } from '../ImageModal/ImageModal';
import { VerticalLogoBanner } from './VerticalLogoBanner';
import StickerWall from './StickerWall';

const PAGE_SIZE = 60;

const Darkside = () => {
  const {
    pictures,
    shuffleImages,
    numberOfTimesToRepeatImages,
    showImageCaptions,
  } = useVal(darksideVal);

  const imageFeed = useMemo(() => {
    return generateImageFeed(
      pictures,
      numberOfTimesToRepeatImages,
      shuffleImages
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(imageFeed.length > PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);
  const [activeImages, setActiveImages] = useState(
    imageFeed.slice(0, PAGE_SIZE)
  );
  const [modalImageIndex, setModalImageIndex] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, document.body.scrollHeight);
  }, []);

  const loadMore = useCallback(() => {
    if (isLoading) return;

    setIsLoading(true);
    const nextPage = currentPage + 1;
    const start = nextPage * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    const nextImages = imageFeed.slice(start, end);

    setActiveImages((prev) => [...prev, ...nextImages]);
    setCurrentPage(nextPage);
    setHasNextPage(end < imageFeed.length);
    setIsLoading(false);
  }, [currentPage, isLoading, imageFeed]);

  const handleImageClick = (index: number) => {
    setModalImageIndex(index);
  };

  const handleImageClose = () => {
    setModalImageIndex(null);
  };

  const navigateToNextImage = () => {
    if (modalImageIndex !== null && modalImageIndex + 1 < imageFeed.length) {
      const nextIndex = modalImageIndex + 1;
      setModalImageIndex(nextIndex);
    }
  };

  const navigateToPreviousImage = () => {
    if (modalImageIndex !== null && modalImageIndex - 1 >= 0) {
      const previousIndex = modalImageIndex - 1;
      setModalImageIndex(previousIndex);
    }
  };

  const [bottomRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 1200px 0px',
  });

  const breakpointColumnsObject = useMemo(
    () => ({
      default: getRandomIntInclusive(2, 5),
      800: getRandomIntInclusive(1, 2),
    }),
    []
  );

  return (
    <div
      className="absolute top-0 bottom-0 left-0 right-0 bg-black overflow-x-hidden"
      ref={rootRef}
    >
      <div className="grid grid-cols-[20px_1fr]">
        <VerticalLogoBanner />
        <div className="w-full min-h-screen bg-black p-4">
          <StickerWall />
          <Masonry
            breakpointCols={breakpointColumnsObject}
            className="flex w-auto"
            columnClassName="ml-4"
          >
            {activeImages.map((item, index) => (
              <FadeInImage
                key={index}
                isPriority={index < 10}
                index={index}
                imageFeedItem={item}
                onImageClick={handleImageClick}
                breakpointColumnsObject={breakpointColumnsObject}
              />
            ))}
          </Masonry>
          {(hasNextPage || isLoading) && <div ref={bottomRef} />}
        </div>
      </div>

      <ImageModal
        showImageCaptions={showImageCaptions}
        image={
          modalImageIndex !== null ? activeImages[modalImageIndex].image : null
        }
        onClose={handleImageClose}
        onNext={navigateToNextImage}
        onPrev={navigateToPreviousImage}
      />
    </div>
  );
};

export default Darkside;
